import React, { useState } from "react";

import {

  Alert,

  Box,

  Button,

  Chip,

  Dialog,

  DialogActions,

  DialogContent,

  DialogTitle,

  Divider,

  Paper,

  Stack,

  TextField,

  Typography,

} from "@mui/material";

import { ClipboardList, Download } from "lucide-react";

import type { TeamCandidate, TeamReview } from "./teamFormationTypes";

import {

  TeamInfoAlert,

  TeamSectionPaper,

  teamBodySx,

  teamPrepareButtonSx,

  teamSectionTitleSx,

  TEAM_MUTED,

  TEAM_TEXT,
  TEAM_PRIMARY,

} from "./teamFormationUi";



type Props = {

  parishName: string;

  seasonLabel: string;

  inConsiderationCount: number;

  authorityLabel: string;

  reviews: TeamReview[];

  candidates: TeamCandidate[];

  onPrepareReview: (payload: {

    reviewBy: string;

    subject: string;

    message: string;

  }) => void;

};



function GovernanceDecision({

  review,

  candidates,

}: {

  review: TeamReview;

  candidates: TeamCandidate[];

}) {

  const [concerns, setConcerns] = useState<Record<string, string>>({});



  if (

    review.status !== "PENDING_REVIEW" &&

    review.status !== "REVIEW_OVERDUE_ESCALATED"

  ) {

    return null;

  }



  const reviewCandidates = candidates.filter(({ coupleId }) =>

    review.candidateIds.includes(coupleId),

  );

  const flagged = Object.entries(concerns)

    .filter(([, comment]) => comment.trim())

    .map(([coupleId, comment]) => ({ coupleId, comment: comment.trim() }));



  return (

    <Paper

      variant="outlined"

      sx={{ p: 1.5, mt: 1.25, borderRadius: "8px", borderColor: "#DEE2E6" }}

    >

      <Typography sx={{ fontWeight: 700, color: TEAM_TEXT }}>

        Governance decision

      </Typography>

      <Typography sx={{ ...teamBodySx, mt: 0.25 }}>

        Review the list as a group. Add a concise comment only for candidates

        with a concern.

      </Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>

        {reviewCandidates.map((candidate) => (

          <Box

            key={candidate.coupleId}

            sx={{

              display: "grid",

              gridTemplateColumns: {

                xs: "1fr",

                sm: "minmax(180px, .7fr) minmax(240px, 1.3fr)",

              },

              gap: 1,

              alignItems: "center",

            }}

          >

            <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>

              {candidate.displayName}

            </Typography>

            <TextField

              size="small"

              label="Concern (optional)"

              value={concerns[candidate.coupleId] ?? ""}

              onChange={(e) =>

                setConcerns((prev) => ({

                  ...prev,

                  [candidate.coupleId]: e.target.value,

                }))

              }

            />

          </Box>

        ))}

      </Stack>

      <Stack

        direction={{ xs: "column", md: "row" }}

        spacing={1}

        sx={{ mt: 1.5 }}

      >

        <Button variant="contained" sx={teamPrepareButtonSx}>

          Approve Proposed List

        </Button>

        <Button

          variant="outlined"

          disabled={!flagged.length}

          sx={teamPrepareButtonSx}

        >

          Approve Except Flagged Couples

        </Button>

        <Button color="warning" sx={teamPrepareButtonSx}>

          Request Changes

        </Button>

      </Stack>

    </Paper>

  );

}



export default function ProspectiveTeamReview({

  parishName,

  seasonLabel,

  inConsiderationCount,

  authorityLabel,

  reviews,

  candidates,

  onPrepareReview,

}: Props) {

  const [dialogOpen, setDialogOpen] = useState(false);

  const [reviewBy, setReviewBy] = useState("");

  const [subject, setSubject] = useState(

    `Team Prospect Review — ${parishName} ${seasonLabel}`,

  );

  const [message, setMessage] = useState(

    `The Lead Couple has prepared prospective Team candidates for ${parishName}'s ${seasonLabel} LOVESTRONG Retreat. The attached review includes prior Retreat attendance and Teams served. Please approve the proposed list or record concerns by the review date. Approved prospects may then be contacted by the Lead Couple.`,

  );



  const openDialog = () => {

    setSubject(`Team Prospect Review — ${parishName} ${seasonLabel}`);

    setMessage(

      `The Lead Couple has prepared prospective Team candidates for ${parishName}'s ${seasonLabel} LOVESTRONG Retreat. The attached review includes prior Retreat attendance and Teams served. Please approve the proposed list or record concerns by the review date. Approved prospects may then be contacted by the Lead Couple.`,

    );

    setDialogOpen(true);

  };



  const handlePrepare = () => {

    onPrepareReview({ reviewBy, subject, message });

    setDialogOpen(false);

  };



  const prepareDisabled = inConsiderationCount === 0;



  return (

    <>

      <TeamSectionPaper>

        <Stack

          direction={{ xs: "column", sm: "row" }}

          spacing={1}

          sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}

        >

          <Box>

            <Typography sx={teamSectionTitleSx}>Prospective Team Review</Typography>

            <Typography sx={{ ...teamBodySx, mt: 0.5 }}>

              Submit selected candidates in one or more auditable review rounds.

              Silence never means approval.

            </Typography>

          </Box>

          <Button

            variant="contained"

            startIcon={<ClipboardList size={16} />}

            disabled={prepareDisabled}

            onClick={openDialog}

            sx={{

              ...teamPrepareButtonSx,

              bgcolor: prepareDisabled ? "#E5E7EB" : TEAM_PRIMARY,

              color: prepareDisabled ? "#6B7280" : "#FFFFFF",

              "&:hover": {

                bgcolor: prepareDisabled ? "#E5E7EB" : "#194E75",

                boxShadow: "none",

              },

              "&.Mui-disabled": {

                bgcolor: "#E5E7EB",

                color: "#6B7280",

              },

            }}

          >

            Prepare Team Prospect Review

          </Button>

        </Stack>



        {reviews.length === 0 && (

          <TeamInfoAlert sx={{ mt: 1.5 }}>

            No review has been prepared yet.

          </TeamInfoAlert>

        )}



        {reviews.map((review) => (

          <Paper

            key={review.id}

            variant="outlined"

            sx={{ p: 1.5, mt: 1.25, borderRadius: "8px", borderColor: "#DEE2E6" }}

          >

            <Stack

              direction={{ xs: "column", sm: "row" }}

              spacing={1}

              sx={{ justifyContent: "space-between" }}

            >

              <Box>

                <Typography sx={{ fontWeight: 700, color: TEAM_TEXT }}>

                  {review.label}

                </Typography>

                <Typography sx={{ fontSize: "0.875rem", color: TEAM_TEXT }}>

                  {review.status.replaceAll("_", " ")} · Review by{" "}

                  {review.reviewBy}

                </Typography>

                <Typography sx={{ fontSize: "0.75rem", color: TEAM_MUTED }}>

                  {review.communicationStatus === "PREPARED_NOT_DELIVERED"

                    ? "Review request prepared; no production email delivered."

                    : review.communicationStatus}

                </Typography>

              </Box>

              <Button startIcon={<Download size={16} />} sx={teamPrepareButtonSx}>

                Download PDF

              </Button>

            </Stack>



            {review.status === "REVIEW_OVERDUE_ESCALATED" && (

              <>

                <Alert severity="warning" sx={{ mt: 1, fontSize: "0.875rem" }}>

                  Review Overdue - Escalated. No decision was recorded by the

                  deadline; the review was not approved automatically.

                </Alert>

                {review.escalationRecipients.length > 0 && (

                  <Stack

                    direction="row"

                    spacing={0.75}

                    sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}

                  >

                    {review.escalationRecipients.map((recipient) => (

                      <Chip

                        key={`${review.id}-${recipient.role}`}

                        size="small"

                        color="warning"

                        variant="outlined"

                        label={`Escalated to ${recipient.role}: ${recipient.displayName}${recipient.email ? "" : " · email missing"}`}

                      />

                    ))}

                  </Stack>

                )}

              </>

            )}



            <GovernanceDecision review={review} candidates={candidates} />

          </Paper>

        ))}

      </TeamSectionPaper>



      <Dialog

        open={dialogOpen}

        onClose={() => setDialogOpen(false)}

        fullWidth

        maxWidth="md"

        aria-labelledby="prepare-team-review-title"

      >

        <DialogTitle id="prepare-team-review-title" sx={{ fontWeight: 700 }}>

          Prepare Team Prospect Review

        </DialogTitle>

        <DialogContent>

          <Stack spacing={1.5} sx={{ pt: 1 }}>

            <TeamInfoAlert sx={{ mt: 0 }}>

              This creates an immutable PDF snapshot and a prepared review

              request. It does not contact candidates or deliver production

              email.

            </TeamInfoAlert>

            <TextField

              label="Approval Authority"

              value={authorityLabel}

              InputProps={{ readOnly: true }}

            />

            <TextField

              required

              type="date"

              label="Review by"

              value={reviewBy}

              onChange={(e) => setReviewBy(e.target.value)}

              InputLabelProps={{ shrink: true }}

            />

            <TextField

              required

              label="Subject"

              value={subject}

              onChange={(e) => setSubject(e.target.value)}

            />

            <TextField

              required

              multiline

              minRows={6}

              label="Review request message"

              value={message}

              onChange={(e) => setMessage(e.target.value)}

              helperText="Editable. Manual changes are preserved."

            />

            <Divider />

            <Typography sx={{ fontSize: "0.875rem", color: TEAM_MUTED }}>

              PDF includes retreat identity, Lead Couple, Retreat Liaison,

              summary, candidate parish, first-time/experienced classification,

              Teams served, and concise history. Phone numbers, email addresses,

              and street addresses are excluded.

            </Typography>

          </Stack>

        </DialogContent>

        <DialogActions>

          <Button onClick={() => setDialogOpen(false)} sx={teamPrepareButtonSx}>

            Cancel

          </Button>

          <Button

            variant="contained"

            disabled={!reviewBy || !subject.trim() || !message.trim()}

            onClick={handlePrepare}

            sx={teamPrepareButtonSx}

          >

            Prepare Review Package

          </Button>

        </DialogActions>

      </Dialog>

    </>

  );

}


